import { parse } from '@eneco/common/date';
import { addLeadingZero } from '@eneco/flows/helpers';
import { Offer } from '@eneco/flows/types';
import {
  Customers_Profile_CustomerProfileResponse,
  DC_Domain_Models_Products_OfferType,
  DC_Domain_Models_Products_ProductType,
  Products_Offers_V3_OfferProductCombinationResponseModel,
  Products_Offers_V3_OfferResponseModel,
} from '@monorepo-types/dc';

import {
  getCashbackValue,
  getOfferContractSpanFromProducts,
  selectCostDetailsByType,
  selectOfferByOfferType,
} from './dc';
import { FlowContextServiceGemak } from '../context';
import { Product } from '../types/sgTypes';

export const getSelectProductCombinationByMarketingProductCode = (
  productCombinations: Products_Offers_V3_OfferProductCombinationResponseModel[],
  marketingProductCode?: string,
): Products_Offers_V3_OfferProductCombinationResponseModel =>
  productCombinations?.find(productCombination =>
    productCombination?.products?.some(product => product?.marketingProductCode === marketingProductCode),
  ) ?? productCombinations[0];

export const getNonCommodityProducts = (
  selectedProductCombination?: Products_Offers_V3_OfferProductCombinationResponseModel | null,
  offer?: Products_Offers_V3_OfferResponseModel,
): Product[] => {
  if (!selectedProductCombination?.products || !offer?.products) {
    return [];
  }

  return selectedProductCombination.products
    .filter(p => p.productType !== 'cashback')
    .map(product => {
      const selectedProduct = offer.products?.find(p => p.marketingProductCode === product.marketingProductCode);

      if (!selectedProduct) {
        return null;
      }

      const costDetails = {
        [`${product.productType}CostDetails`]: selectedProduct.productRateDetails?.reduce(
          (acc, item) => ({
            ...acc,
            [item.type]: {
              price: {
                vat: item.vat,
                vatExcluded: item.vatExcluded,
                vatIncluded: item.vatIncluded,
              },
              components: item.components,
            },
          }),
          {},
        ),

        [`${product.productType}EnergyTaxDetails`]: selectedProduct.energyTaxDetails?.tariffs?.reduce(
          (acc, item) => ({
            ...acc,
            [`bracketNr${item.bracketNr}`]: {
              active: item.usage > 0,
              usageMax: item.bracketUsageMax,
              usageMin: item.bracketUsageMin,
              price: {
                vatAmount: item.vatAmount,
                vatExcluded: item.vatExcluded,
                vatIncluded: item.vatIncluded,
              },
            },
          }),
          {},
        ),

        [`${product.productType}Total`]: selectedProduct.costDetails?.reduce(
          (acc, item) => ({
            ...acc,
            [item.type ?? '']: item.costs,
          }),
          {},
        ),
      };

      return {
        type: selectedProduct.productType,
        code: selectedProduct.marketingProductCode,
        productCombinationCode: selectedProductCombination.discountCode ?? undefined,
        variant: selectedProductCombination.discountCode ?? undefined,
        description: selectedProduct.description,
        contractSpan: selectedProduct.contractSpan,
        costsMonthly: selectCostDetailsByType('monthlyCosts', selectedProduct.costDetails),
        costsYearly: selectCostDetailsByType('yearlyCosts', selectedProduct.costDetails),
        costDetails,
        contractStartDate: offer.contractStartDate ?? undefined,
        contractEndDate: selectedProduct.contractEndDate ?? undefined,
      };
    })
    .filter(Boolean) as Product[];
};

export const getNonCommodityOffer = (
  flowContext: FlowContextServiceGemak,
  marketingProductCode: string,
  offer?: Products_Offers_V3_OfferResponseModel,
): Omit<Offer, 'mostPopularChoice'> | undefined => {
  const { offerType } = flowContext;

  if (!offer?.productCombinations || !offerType) {
    return undefined;
  }

  const selectedOffer = selectOfferByOfferType(offer, offerType);
  const selectedProductCombination = getSelectProductCombinationByMarketingProductCode(
    offer.productCombinations,
    marketingProductCode,
  );

  if (!selectedProductCombination) {
    return undefined;
  }

  const nonCommodityProducts = getNonCommodityProducts(selectedProductCombination, offer);

  const selectedProductCombinationMonthlyCosts = selectCostDetailsByType(
    'monthlyCosts',
    selectedProductCombination.costDetails,
  );
  const selectedProductCombinationYearlyCosts = selectCostDetailsByType(
    'yearlyCosts',
    selectedProductCombination.costDetails,
  );
  const selectedProductCombinationYearlyPromotionCosts = selectCostDetailsByType(
    'yearlyCostsPromotion',
    selectedProductCombination.costDetails,
  );

  const selectedProductCombinationCashbackProduct = selectedProductCombination.products?.find(
    p => p.productType === 'cashback',
  );

  const selectedProductCombinationCashbackProductCosts =
    selectedProductCombinationCashbackProduct && getCashbackValue(selectedProductCombination, offer);

  const hasYearlyPromotionCosts =
    selectedProductCombinationYearlyCosts?.vatExcluded !== selectedProductCombinationYearlyPromotionCosts?.vatExcluded;

  return {
    type: offerType,
    products: nonCommodityProducts,
    costsMonthly: selectedProductCombinationMonthlyCosts,
    costsYearly: selectedProductCombinationYearlyCosts,
    ...(hasYearlyPromotionCosts && {
      costsYearlyPromotion: selectedProductCombinationYearlyPromotionCosts,
    }),
    costDetails: {
      ...selectedProductCombination.costDetails,
      cashback: selectedProductCombinationCashbackProductCosts,
    },
    costDiscounts: {
      cashback: selectedProductCombinationCashbackProductCosts,
    },
    contractSpan: getOfferContractSpanFromProducts(nonCommodityProducts),
    contractStartDate: offer.contractStartDate ?? undefined,
    contractEndDate: selectedOffer?.contractEndDate ?? undefined,
  };
};

export const getOfferTermsAndConditions = (
  context: Pick<FlowContextServiceGemak, 'sgProduct'>,
  offer?: Products_Offers_V3_OfferResponseModel,
) => {
  return offer?.productCombinations?.find(combination => combination.discountCode === context.sgProduct)?.terms ?? [];
};

export const getProductTypesFromOfferType = (
  offerType?: DC_Domain_Models_Products_OfferType,
): Array<DC_Domain_Models_Products_ProductType> => {
  switch (offerType) {
    case 'KetelComfort':
      return ['ketelComfort'];
    case 'ServiceGemak':
      return ['servicegemak'];
    case 'RentalDevices':
      return ['huurapparaat'];
    default:
      return [];
  }
};

export const getNonCommodityOffers = (
  productType: DC_Domain_Models_Products_ProductType,
  offer?: Products_Offers_V3_OfferResponseModel,
) => {
  return offer?.products?.filter(product => product.productType === productType) ?? [];
};

export const getMergedUserData = (
  userDetailsContext: Pick<
    FlowContextServiceGemak,
    | 'birthdate'
    | 'salutation'
    | 'firstName'
    | 'surname'
    | 'houseNumber'
    | 'houseNumberSuffix'
    | 'postalCode'
    | 'phoneNumber'
    | 'emailAddress'
  >,
  userDetailsAccount?: Customers_Profile_CustomerProfileResponse,
) => {
  const birthdate = userDetailsContext?.birthdate ?? userDetailsAccount?.person?.dateOfBirth;
  const day = birthdate ? addLeadingZero(parse(birthdate).getDate()) : '';
  const month = birthdate ? addLeadingZero(parse(birthdate ?? '').getMonth() + 1) : '';
  const year = parse(birthdate ?? '').getFullYear() || '';

  const mergedUserDetails = {
    phoneNumber: userDetailsContext?.phoneNumber ?? userDetailsAccount?.contact?.mobilePhoneNumber ?? '',
    emailAddress: userDetailsContext?.emailAddress ?? userDetailsAccount?.contact?.emailAddress ?? '',
    firstName: userDetailsContext?.firstName ?? userDetailsAccount?.person?.firstName ?? '',
    surname: userDetailsContext?.surname ?? userDetailsAccount?.person?.surname ?? '',
    salutation: userDetailsContext?.salutation ?? userDetailsAccount?.person?.salutation?.toLowerCase() ?? '',
    day: day,
    month: month,
    year: year,
  };

  return mergedUserDetails;
};
